/**
 * Catálogo público de billing (RPC get_public_billing_catalog).
 * Sin dependencias de React — importable desde Next y Expo.
 */

import { PLAN_MARKETING_BULLETS, normalizePlanCode } from './plan-benefits';

export type PlanCatalogItem = {
  plan_code: string;
  display_name: string;
  price_ars_monthly: number;
  max_products?: number | null;
  max_branches?: number | null;
  max_users?: number | null;
  monthly_pos_transactions_limit?: number | null;
  monthly_online_orders_limit?: number | null;
  monthly_appointments_limit?: number | null;
};

export type SignupMaxTrialConfig = {
  enabled: boolean;
  days: number;
  plan_code: string;
};

export type PublicBillingCatalog = {
  plans: PlanCatalogItem[];
  signup_max_trial: SignupMaxTrialConfig;
  paid_plan_trial_days: number;
};

export type ActivePlanGrant = {
  plan_code: string;
  expires_at: string;
  source: string;
};

export const DEFAULT_BILLING_CATALOG: PublicBillingCatalog = {
  plans: [
    {
      plan_code: 'gratis',
      display_name: 'Gratis',
      price_ars_monthly: 0,
      max_products: null,
      max_branches: 1,
      max_users: 1,
      monthly_pos_transactions_limit: 250,
      monthly_online_orders_limit: 25,
      monthly_appointments_limit: 25,
    },
    {
      plan_code: 'emprende',
      display_name: 'Emprende',
      price_ars_monthly: 10900,
      max_products: null,
      max_branches: 1,
      max_users: 1,
      monthly_pos_transactions_limit: 1000,
      monthly_online_orders_limit: 100,
      monthly_appointments_limit: 100,
    },
    {
      plan_code: 'crece',
      display_name: 'Crece',
      price_ars_monthly: 25000,
      max_products: null,
      max_branches: null,
      max_users: null,
      monthly_pos_transactions_limit: null,
      monthly_online_orders_limit: null,
      monthly_appointments_limit: null,
    },
  ],
  signup_max_trial: { enabled: false, days: 14, plan_code: 'crece' },
  paid_plan_trial_days: 14,
};

function normalizeLimit(value: unknown, fallback: number | null | undefined): number | null {
  if (value === undefined) return fallback ?? null;
  if (value === null) return null;
  const limit = Number(value);
  return Number.isFinite(limit) && limit >= 0 ? limit : fallback ?? null;
}

function formatMonthlyLimit(limit: number | null | undefined, unit: string): string {
  const amount = limit == null ? null : new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(limit);
  return amount == null ? `${unit} sin límite` : `Hasta ${amount} ${unit} al mes`;
}

export function formatArs(amount: number): string {
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
}

export function normalizePublicBillingCatalog(data: unknown): PublicBillingCatalog {
  if (!data || typeof data !== 'object') {
    return DEFAULT_BILLING_CATALOG;
  }
  const raw = data as Record<string, unknown>;
  const plansRaw = Array.isArray(raw.plans) ? raw.plans : [];
  const plans: PlanCatalogItem[] = plansRaw
    .map((p): PlanCatalogItem | null => {
      if (!p || typeof p !== 'object') return null;
      const row = p as Record<string, unknown>;
      const planCode = String(row.plan_code ?? '').trim();
      if (!planCode) return null;
      const fallback = DEFAULT_BILLING_CATALOG.plans.find((plan) => plan.plan_code === planCode);
      return {
        plan_code: planCode,
        display_name: String(row.display_name ?? fallback?.display_name ?? planCode),
        price_ars_monthly: Math.max(0, Number(row.price_ars_monthly ?? fallback?.price_ars_monthly ?? 0)),
        max_products: normalizeLimit(row.max_products, fallback?.max_products),
        max_branches: normalizeLimit(row.max_branches, fallback?.max_branches),
        max_users: normalizeLimit(row.max_users, fallback?.max_users),
        monthly_pos_transactions_limit: normalizeLimit(
          row.monthly_pos_transactions_limit,
          fallback?.monthly_pos_transactions_limit,
        ),
        monthly_online_orders_limit: normalizeLimit(
          row.monthly_online_orders_limit,
          fallback?.monthly_online_orders_limit,
        ),
        monthly_appointments_limit: normalizeLimit(
          row.monthly_appointments_limit,
          fallback?.monthly_appointments_limit,
        ),
      };
    })
    .filter((p): p is PlanCatalogItem => p !== null);

  const signupRaw =
    raw.signup_max_trial && typeof raw.signup_max_trial === 'object'
      ? (raw.signup_max_trial as Record<string, unknown>)
      : {};

  return {
    plans: plans.length > 0 ? plans : DEFAULT_BILLING_CATALOG.plans,
    signup_max_trial: {
      enabled: signupRaw.enabled === true,
      days: Math.max(0, Number(signupRaw.days ?? 0)),
      plan_code: String(signupRaw.plan_code ?? 'crece').trim() || 'crece',
    },
    paid_plan_trial_days: Math.max(0, Number(raw.paid_plan_trial_days ?? 14)),
  };
}

export function planCatalogItem(
  catalog: PublicBillingCatalog,
  planCode: string,
): PlanCatalogItem | null {
  const code = planCode.trim().toLowerCase();
  return catalog.plans.find((p) => p.plan_code.toLowerCase() === code) ?? null;
}

export function planPriceArs(catalog: PublicBillingCatalog, planCode: string): number {
  return planCatalogItem(catalog, planCode)?.price_ars_monthly ?? 0;
}

export function planPriceLabel(catalog: PublicBillingCatalog, planCode: string): string {
  const price = planPriceArs(catalog, planCode);
  if (planCode === 'gratis' || price <= 0) {
    return '$0';
  }
  return `${formatArs(price)} / mes`;
}

/** Subtítulo bajo el precio en tarjetas de plan (canal de cobro visible). */
export function planBillingPriceHint(surface: 'native-store' | 'web-mercadopago'): string {
  if (surface === 'web-mercadopago') return 'Cobro mensual con Mercado Pago';
  return 'Precio en la tienda del celular';
}

export function nativeStoreBillingLabel(platform: 'ios' | 'android'): string {
  return platform === 'ios' ? 'App Store' : 'Google Play';
}

export function signupMaxTrialHeadline(catalog: PublicBillingCatalog): string | null {
  const cfg = catalog.signup_max_trial;
  if (!cfg.enabled || cfg.days <= 0) return null;
  const planName =
    cfg.plan_code === 'crece'
      ? 'Crece'
      : cfg.plan_code;
  return `${cfg.days} días de ${planName} gratis al registrarte (sin tarjeta)`;
}

export function paidPlanTrialHeadline(catalog: PublicBillingCatalog): string | null {
  if (catalog.signup_max_trial.enabled) return null;
  const days = catalog.paid_plan_trial_days;
  if (days <= 0) return null;
  return `${days} días de prueba gratis`;
}

/** Bullets de marketing para tarjetas de plan (landing / billing). */
export function planMarketingBullets(
  catalog: PublicBillingCatalog,
  planCode: string | undefined | null,
): string[] {
  const n = normalizePlanCode(planCode);
  if (n === 'other') {
    return ['Incluye los límites del plan asignado a tu organización.'];
  }

  const base = [...PLAN_MARKETING_BULLETS[n]];
  const plan = planCatalogItem(catalog, n);
  const limits = n === 'gratis' || n === 'emprende'
    ? [
        formatMonthlyLimit(plan?.monthly_pos_transactions_limit, 'ventas POS'),
        formatMonthlyLimit(plan?.monthly_online_orders_limit, 'pedidos online'),
        formatMonthlyLimit(plan?.monthly_appointments_limit, 'turnos'),
      ]
    : [];
  const bullets = [...base, ...limits];
  const signup = catalog.signup_max_trial;

  if (signup.enabled && signup.days > 0) {
    return signup.plan_code === n ? [signupMaxTrialHeadline(catalog)!, ...bullets] : bullets;
  }

  const trialLine = paidPlanTrialHeadline(catalog);
  return trialLine && n === 'crece' ? [trialLine, ...bullets] : bullets;
}

export function planTrialSubtext(
  catalog: PublicBillingCatalog,
  planCode: PaidPlanCodeLike,
  isCurrent: boolean,
): string | null {
  if (isCurrent) return null;
  if (catalog.signup_max_trial.enabled) {
    return null;
  }
  const n = normalizePlanCode(planCode);
  if (n !== 'crece') return null;
  return paidPlanTrialHeadline(catalog);
}

export type PaidPlanCodeLike = 'crece' | string;

export function parseActivePlanGrant(value: unknown): ActivePlanGrant | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const planCode = String(row.plan_code ?? '').trim();
  const expiresAt = String(row.expires_at ?? '').trim();
  if (!planCode || !expiresAt) return null;
  return {
    plan_code: planCode,
    expires_at: expiresAt,
    source: String(row.source ?? ''),
  };
}

/** Días enteros restantes hasta el vencimiento del grant (0 si ya venció). */
export function grantDaysRemaining(expiresAt: string, now: number = Date.now()): number {
  try {
    const end = new Date(expiresAt).getTime();
    if (Number.isNaN(end)) return 0;
    const ms = end - now;
    if (ms <= 0) return 0;
    return Math.ceil(ms / 86_400_000);
  } catch {
    return 0;
  }
}

/** Texto corto del tiempo restante de prueba/promo (para banners compactos). */
export function grantRemainingLabel(expiresAt: string, now: number = Date.now()): string {
  const days = grantDaysRemaining(expiresAt, now);
  if (days <= 0) return 'Vence hoy';
  if (days === 1) return 'Queda 1 día';
  return `Quedan ${days} días`;
}

export function formatGrantExpiryLabel(expiresAt: string): string {
  try {
    const d = new Date(expiresAt);
    if (Number.isNaN(d.getTime())) return expiresAt;
    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return expiresAt;
  }
}

export function activePlanGrantBannerText(grant: ActivePlanGrant | null | undefined): string | null {
  if (!grant) return null;
  const planName = planDisplayName(grant.plan_code);
  return `Plan ${planName} gratis hasta el ${formatGrantExpiryLabel(grant.expires_at)}.`;
}

export function planDisplayName(planCode: string | undefined | null): string {
  const code = (planCode ?? '').trim().toLowerCase();
  if (code === 'emprende') return 'Emprende';
  if (code === 'crece') return 'Crece';
  if (code === 'gratis') return 'Gratis';
  if (code === 'legacy') return 'Plan original';
  // Compatibilidad legacy
  if (code === 'completo') return 'Plan original';
  if (code === 'esencial') return 'Plan original';
  return planCode?.trim() || 'Plan';
}

/** Toast de bienvenida tras registrar una org con campaña signup trial activa. */
export function signupTrialWelcomeCopy(
  days: number,
  planCode: string | undefined | null,
): { title: string; body: string } {
  const planName = planDisplayName(planCode);
  return {
    title: `${days} días de plan ${planName}, gratis`,
    body:
      'Activamos tu prueba sin cargo. Cuando termine seguís en Gratis o elegís un plan desde Perfil → Plan y facturación.',
  };
}

export type TrialUrgencyPhase = 'normal' | 'soon' | 'last_day' | 'expired';

export function trialUrgencyPhase(expiresAt: string, now: number = Date.now()): TrialUrgencyPhase {
  const days = grantDaysRemaining(expiresAt, now);
  if (days <= 0) return 'expired';
  if (days <= 1) return 'last_day';
  if (days <= 3) return 'soon';
  return 'normal';
}

export function trialBannerSubtitle(expiresAt: string, planName: string, now: number = Date.now()): string {
  const phase = trialUrgencyPhase(expiresAt, now);
  const days = grantDaysRemaining(expiresAt, now);
  if (phase === 'last_day') {
    return days <= 0 ? 'Vence hoy' : 'Vence mañana';
  }
  if (phase === 'soon') {
    return `Quedan ${days} días`;
  }
  return grantRemainingLabel(expiresAt, now);
}

export function parseExpiredSignupGrant(value: unknown): ActivePlanGrant | null {
  if (value == null || value === 'null') return null;
  return parseActivePlanGrant(value);
}

export type TrialExpiredPromptCopy = {
  title: string;
  body: string;
  bullets: string[];
  choosePlanLabel: string;
  dismissLabel: string;
};

export function trialExpiredPromptCopy(grant: ActivePlanGrant | null | undefined): TrialExpiredPromptCopy {
  const planName = planDisplayName(grant?.plan_code ?? 'crece');
  return {
    title: `Tu prueba de ${planName} terminó`,
    body: `Seguís en el plan Gratis. POS, stock y Mercado Pago en el mostrador siguen. Lo que era exclusivo del plan ${planName} vuelve a los límites del plan gratuito.`,
    bullets: [
      '1 local y 1 usuario (sin invitar empleados)',
      'Sin link de cobro de fiado',
      'Sin multi-sucursal, auditoría, fiscal ni promociones',
      'Ventas, pedidos y turnos con el tope del plan gratis',
    ],
    choosePlanLabel: 'Activar plan Crece',
    dismissLabel: 'Entendido',
  };
}

export type LapsedSubscription = {
  plan_code: string;
  provider: string;
  status: string;
  expires_at?: string | null;
};

export function parseLapsedSubscription(value: unknown): LapsedSubscription | null {
  if (value == null || value === 'null') return null;
  if (typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const planCode = String(row.plan_code ?? '').trim().toLowerCase();
  const provider = String(row.provider ?? '').trim();
  const status = String(row.status ?? '').trim();
  if (!planCode || !provider || !status) return null;
  const expiresAt = row.expires_at;
  return {
    plan_code: planCode,
    provider,
    status,
    expires_at: expiresAt == null ? null : String(expiresAt),
  };
}

/** Nombre del canal de cobro para copy user-facing (Mercado Pago / App Store / Google Play). */
export function subscriptionProviderLabel(provider: string | undefined | null): string {
  const p = (provider ?? '').trim().toLowerCase();
  if (p === 'mercadopago') return 'Mercado Pago';
  if (p === 'apple') return 'App Store';
  if (p === 'google') return 'Google Play';
  return provider?.trim() || 'tu tienda';
}

export function subscriptionLapsedPromptCopy(
  lapsed: LapsedSubscription | null | undefined,
): TrialExpiredPromptCopy {
  const planName = planDisplayName(lapsed?.plan_code ?? 'crece');
  const providerLabel = subscriptionProviderLabel(lapsed?.provider);
  return {
    title: `Tu suscripción ${planName} finalizó`,
    body: `Volviste al plan Gratis. Tus datos se conservan. La suscripción ${planName} de ${providerLabel} terminó; lo exclusivo queda desactivado hasta que actives un plan de pago.`,
    bullets: [
      '1 local y 1 usuario (sin invitar empleados)',
      'Sin link de cobro de fiado',
      'Sin multi-sucursal, auditoría, fiscal ni promociones',
      `Podés reactivar desde ${providerLabel} o elegir un plan acá`,
    ],
    choosePlanLabel: 'Activar plan Crece',
    dismissLabel: 'Entendido',
  };
}

export function trialExpiredReminderBody(grant: ActivePlanGrant | null | undefined): string {
  return 'Estás en Gratis. Elegí Crece si necesitás equipo, varios locales o sin tope de volumen.';
}

export function subscriptionLapsedReminderBody(
  lapsed: LapsedSubscription | null | undefined,
): string {
  const planName = planDisplayName(lapsed?.plan_code ?? 'crece');
  return `${planName} quedó inactivo. Estás en Gratis con los límites del plan — reactivá cuando quieras.`;
}

export function activePlanGrantUrgencyBannerText(
  grant: ActivePlanGrant | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!grant) return null;
  const planName = planDisplayName(grant.plan_code);
  const base = activePlanGrantBannerText(grant);
  const phase = trialUrgencyPhase(grant.expires_at, now);
  if (phase === 'normal' || !base) return base;
  return `${base} ${trialBannerSubtitle(grant.expires_at, planName, now)}.`;
}
